const SUPPORTED_FIELDS = new Set([
  'article_name', 'commercial_name', 'ingredient_name', 'product_code',
  'lot_number', 'document_ref', 'production_date', 'received_date',
  'expiry_date', 'quantity', 'ingredients', 'allergens', 'storage', 'company',
  'qr_payload',
]);

const ELEMENT_TYPES = new Set(['static', 'dynamic', 'image', 'qr', 'line', 'rectangle']);
const ALIGNS = new Set(['left', 'center', 'right']);

function fail(message) {
  throw new Error(message);
}

function numberInRange(value, label, min, max) {
  const number = Number(value);
  if (!Number.isFinite(number) || number < min || number > max) fail(`${label} non valido`);
  return number;
}

function normalizeElement(element, index, width, height, seenIds, fields) {
  if (!element || typeof element !== 'object' || Array.isArray(element)) fail(`Elemento ${index + 1} non valido`);
  const id = String(element.id || '').trim();
  if (!id || id.length > 80 || seenIds.has(id)) fail(`ID mancante o duplicato nell’elemento ${index + 1}`);
  seenIds.add(id);

  const type = String(element.type || '');
  if (!ELEMENT_TYPES.has(type)) fail(`Tipo elemento non supportato: ${type || '(vuoto)'}`);
  const x_mm = numberInRange(element.x_mm, `Posizione X elemento ${index + 1}`, 0, width);
  const y_mm = numberInRange(element.y_mm, `Posizione Y elemento ${index + 1}`, 0, height);
  const w_mm = numberInRange(element.w_mm, `Larghezza elemento ${index + 1}`, 0.1, width);
  const h_mm = numberInRange(element.h_mm, `Altezza elemento ${index + 1}`, 0.1, height);
  if (x_mm + w_mm > width + 0.01 || y_mm + h_mm > height + 0.01) {
    fail(`L’elemento ${index + 1} supera i bordi dell’etichetta`);
  }

  const normalized = { id, type, x_mm, y_mm, w_mm, h_mm };
  if (type === 'static' || type === 'dynamic') {
    const font_size = numberInRange(element.font_size ?? 6, `Font elemento ${index + 1}`, 0.75, 72);
    const align = String(element.align || 'left');
    if (!ALIGNS.has(align)) fail(`Allineamento non valido nell’elemento ${index + 1}`);
    normalized.font_size = font_size;
    normalized.bold = Boolean(element.bold);
    normalized.align = align;
    normalized.fit_mode = element.fit_mode === 'expand' ? 'expand' : 'shrink';
    if (element.color === 'red') normalized.color = 'red';
    if (type === 'static') {
      if (typeof element.content !== 'string' || element.content.length > 2000 || element.content.includes('\n')) {
        fail(`Testo non valido nell’elemento ${index + 1}`);
      }
      normalized.content = element.content;
    } else {
      const field = String(element.field || '');
      if (!SUPPORTED_FIELDS.has(field) || field === 'qr_payload') fail(`Dato automatico non supportato nell’elemento ${index + 1}`);
      normalized.field = field;
      fields[field] = true;
    }
  } else if (type === 'image') {
    const content = String(element.content || '');
    const imageData = content.match(/^data:image\/(png|jpeg|webp);base64,([A-Za-z0-9+/]+={0,2})$/);
    const signatures = { png: 'iVBORw0KGgo', jpeg: '/9j/', webp: 'UklGR' };
    if (content.length > 1_000_000 || !imageData || !imageData[2].startsWith(signatures[imageData[1]])) {
      fail(`Immagine non valida nell’elemento ${index + 1}; sono supportate immagini PNG, JPEG o WebP incorporate`);
    }
    try { atob(imageData[2]); } catch { fail(`Dati immagine non validi nell’elemento ${index + 1}`); }
    normalized.content = content;
  } else if (type === 'qr') {
    const field = String(element.field || 'qr_payload');
    if (field !== 'qr_payload') fail(`Il QR deve usare il dato qr_payload (elemento ${index + 1})`);
    normalized.field = field;
    fields.qr_payload = true;
  } else {
    normalized.stroke_width = numberInRange(element.stroke_width ?? 0.25, `Spessore elemento ${index + 1}`, 0.1, 3);
  }
  return normalized;
}

function normalizeTemplate(template, index) {
  if (!template || typeof template !== 'object' || Array.isArray(template)) fail(`Template ${index + 1} non valido`);
  const name = String(template.name || '').trim();
  if (!name || name.length > 120) fail(`Nome mancante o troppo lungo nel template ${index + 1}`);
  const layout = template.layout;
  if (!layout || typeof layout !== 'object' || Array.isArray(layout)) fail(`Layout mancante nel template “${name}”`);
  const width_mm = numberInRange(layout.width_mm, `Larghezza “${name}”`, 20, 500);
  const height_mm = numberInRange(layout.height_mm, `Altezza “${name}”`, 20, 500);
  if (!Array.isArray(layout.elements) || layout.elements.length < 1 || layout.elements.length > 100) {
    fail(`Il template “${name}” deve contenere da 1 a 100 elementi`);
  }

  const fields = {};
  const declaredFields = template.fields && typeof template.fields === 'object' && !Array.isArray(template.fields)
    ? template.fields
    : {};
  Object.entries(declaredFields).forEach(([field, enabled]) => {
    if (enabled === true && SUPPORTED_FIELDS.has(field)) fields[field] = true;
  });
  const seenIds = new Set();
  const elements = layout.elements.map((element, elementIndex) =>
    normalizeElement(element, elementIndex, width_mm, height_mm, seenIds, fields));

  return {
    name,
    fields,
    custom_text: typeof template.custom_text === 'string' ? template.custom_text.slice(0, 2000) : '',
    design_only: template.design_only === true,
    layout: { width_mm, height_mm, elements },
  };
}

export function parseLabelTemplateBundle(text) {
  let document;
  try {
    document = JSON.parse(text);
  } catch {
    fail('Il file non contiene JSON valido');
  }

  if (document?.schema_version != null && document.schema_version !== 1) {
    fail(`Versione file non supportata: ${document.schema_version}`);
  }
  const source = Array.isArray(document)
    ? document
    : Array.isArray(document?.templates)
      ? document.templates
      : document?.template
        ? [document.template]
        : [document];
  if (source.length < 1 || source.length > 10) fail('Il file deve contenere da 1 a 10 template');

  const templates = source.map(normalizeTemplate);
  const names = new Set();
  templates.forEach(template => {
    const key = template.name.toLocaleLowerCase('it-IT');
    if (names.has(key)) fail(`Il file contiene più template chiamati “${template.name}”`);
    names.add(key);
  });
  return templates;
}
